/**
 * MSW handlers — let the app run standalone against synthetic data with no
 * live M/P services. Handlers match the same URLs the RTK Query clients call,
 * built from VITE_OXBO_M_URL / VITE_OXBO_P_URL, and return the SAME wire shapes
 * (snake_case WellOut / EventOut / DailyReportOut / pivots) as the real
 * services — so flipping VITE_USE_MOCKS=false is a true drop-in swap.
 */
import { http, HttpResponse } from "msw";
import type {
  WellOut,
  DailyReportOut,
  DailyReportCreate,
  DailyReportUpdate,
  InboxResponse,
  CostPivotResponse,
  NptRollupResponse,
  PlanVsActualResponse,
  ReportCloseResponse,
  AfeLineOut,
  AfeLineCreate,
  AfeLineUpdate,
  SavedQueryOut,
  SavedQueryCreate,
} from "@api/types";
import type { CatalogResponse } from "@api/types";
import { capsSatisfy } from "@api/capabilities";
import {
  WELLS,
  WELL_BY_ID,
  EVENTS_BY_WELL_UWI,
  REPORTS_BY_ID,
  REPORTS_BY_EVENT,
  COST_PIVOTS,
  PLAN_VS_ACTUAL,
  NPT_ROLLUPS,
  INBOX,
  CATALOGS,
  SAVED_QUERIES,
} from "./seed";
import { FIELD_UNITS, PROFILE_LIST, RESOLVED_PROFILES } from "./unitsSeed";
import {
  SURVEYS,
  TUBULAR_STRINGS,
  PIPE_TALLY,
  BHA_RUNS,
  BHA_COMPONENTS,
  BIT_OPS,
  MUD_CHECKS,
  MUD_PRODUCTS,
  MUD_TRANSACTIONS,
  MUD_VOLUMES,
  SAFETY_DAYS,
  ACTIVITIES,
  SAFETY_ROLLUPS,
  AFE_LINES,
  PAD_ROLLUPS,
  PAD_SHARED_COSTS,
  computeMockSplit,
  TIMELINE_NODES,
  PLAN_TIMELINES,
  CATALOG_VALUES,
} from "./domainSeed";
import type { PadSharedCostCreate, PadSharedCostUpdate } from "@api/types";

const M = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";
const P = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

// A mutable copy so create/patch/close mutations persist within a session.
const reports: Record<string, DailyReportOut> = { ...REPORTS_BY_ID };
const reportsByEvent: Record<string, DailyReportOut[]> = Object.fromEntries(
  Object.entries(REPORTS_BY_EVENT).map(([k, v]) => [k, [...v]]),
);

// Mutable AFE-line store so create/edit persist within a session.
const afeByEvent: Record<string, AfeLineOut[]> = Object.fromEntries(
  Object.entries(AFE_LINES).map(([k, v]) => [k, v.map((l) => ({ ...l }))]),
);

/**
 * ADJUSTED_COST (§21f) — the source-of-truth accrual (devengado) rule, adopted
 * verbatim from Aura's `afe_x_avance.py` so the mock server computes accrual the
 * same way P will. The FE never runs this; it lives server-side (here, in the
 * mock). `tratamiento` comes from the cost account (AVANCE | DEVENGADO, blank ⇒
 * Avance). Method refines it: lump/milestone accrue their full budget only once
 * their event lands (approved); pct_complete keeps whatever accrued was set;
 * by_quantity accrues qty × rate.
 */
function computeAccrued(line: AfeLineOut): number {
  const acct = CATALOG_VALUES["cost_accounts"]?.values.find(
    (v) => v.code === line.account_entry_key,
  );
  const tratamiento = String(acct?.attrs?.tratamiento ?? "").toUpperCase() || "AVANCE";
  const plan = line.budget;
  switch (line.accrual_method) {
    case "by_quantity":
      return Math.round((line.qty ?? 0) * (line.rate ?? 0));
    case "lump":
    case "milestone":
      // Full budget accrues once the milestone lands; DEVENGADO needs actual too.
      if (tratamiento === "DEVENGADO") return line.actual > 0 ? plan : 0;
      return line.status === "approved" ? plan : 0;
    case "pct_complete":
      return line.accrued;
    default:
      return line.accrued;
  }
}
// Saved queries: mutable session store + the demo user id (matches devAuth's
// DEV_USER_ID) so newly-created rows land under "Mine".
let savedQueries: SavedQueryOut[] = [...SAVED_QUERIES];
const MOCK_USER_ID = "00000000-0000-0000-0000-0000000000d0";
const MOCK_ORG_ID = "00000000-0000-0000-0000-0000000000a0";

/** The capability dictionary the /acl screen lists (mirrors Oxbo-M vocab). */
const CAPABILITY_DICT = [
  { capability_key: "well:create", display_label: "Crear pozo (Crear Workover / Drilling)" },
  { capability_key: "event:create", display_label: "Crear evento" },
  { capability_key: "import:run", display_label: "Importar (workover/parte diario)" },
  { capability_key: "catalog:*:write", display_label: "Administrar catálogos" },
  { capability_key: "lineage:view", display_label: "Ver linaje de datos" },
  { capability_key: "report:generate", display_label: "Generar reportes" },
  { capability_key: "afe:manage", display_label: "Administrar AFE" },
  { capability_key: "acl:manage", display_label: "Administrar permisos" },
];

/** Mutable per-user capability store so grant/revoke persist in a session. */
const capStore: Record<string, string[]> = {
  [MOCK_USER_ID]: [
    "well:create",
    "event:create",
    "import:run",
    "catalog:*:write",
    "lineage:view",
    "report:generate",
    "afe:manage",
    "acl:manage",
  ],
};
const capsFor = (userId: string): string[] => [...(capStore[userId] ?? [])].sort();

function filterWells(url: URL): WellOut[] {
  const q = url.searchParams;
  const status = q.get("status") ?? "";
  const activeOnly = q.get("active_only") === "true";
  return WELLS.filter((w) => {
    if (status && w.status !== status) return false;
    if (activeOnly && !w.is_active) return false;
    return true;
  });
}

export const handlers = [
  // ─────────────────────────── Oxbo-M ───────────────────────────
  http.get(`${M}/v1/wells`, ({ request }) => {
    return HttpResponse.json(filterWells(new URL(request.url)));
  }),
  http.get(`${M}/v1/wells/:id`, ({ params }) => {
    const well = WELL_BY_ID[params.id as string];
    return well ? HttpResponse.json(well) : new HttpResponse(null, { status: 404 });
  }),
  http.get(`${M}/v1/categories/:key/resolved`, ({ params }) => {
    const cat = CATALOGS[params.key as string];
    return cat
      ? HttpResponse.json(cat)
      : HttpResponse.json({ category_key: params.key, generation: "gen-0", entries: [] });
  }),

  // Catalog value-API (§22 / §23). Server-side typeahead (`q=`) + the §22c
  // security gate: the `admin` block is STRIPPED unless the caller is
  // authorised. The mock treats a request carrying `?_demo_admin=1` or an
  // `X-Oxbo-Caps` header holding the catalog's capability as authorised — a
  // stand-in for the real token-claim check. A field user's request never
  // receives the URL, so it cannot leak via devtools.
  http.get(`${M}/v1/catalogs/:key`, ({ params, request }) => {
    const src = CATALOG_VALUES[params.key as string];
    if (!src) {
      return HttpResponse.json({ key: params.key, label: params.key, values: [] });
    }
    const url = new URL(request.url);
    const q = (url.searchParams.get("q") ?? "").trim().toLowerCase();
    let values = src.values;
    if (q) {
      values = values.filter(
        (v) =>
          v.label.toLowerCase().includes(q) ||
          v.code.toLowerCase().includes(q) ||
          (v.aliases ?? []).some((a) => a.toLowerCase().includes(q)),
      );
    }
    const caps = (request.headers.get("X-Oxbo-Caps") ?? "").split(",").filter(Boolean);
    const demoAdmin = url.searchParams.get("_demo_admin") === "1";
    const authorised =
      demoAdmin || (src.admin ? capsSatisfy(caps, src.admin.capability) : false);
    // §22c: omit the whole `admin` block for non-authorised callers.
    const { admin, ...rest } = src;
    const body: CatalogResponse =
      authorised && admin ? { ...rest, admin, values } : { ...rest, values };
    return HttpResponse.json(body);
  }),

  // ── Capabilities (ACL foundation, Oxbo-M) ──
  // The demo user (owner) holds the full admin set PLUS acl:manage, so the demo
  // can exercise the /acl screen. A mutable per-user grant store lets grant/
  // revoke persist within a session; /me reflects the demo user's own set.
  http.get(`${M}/v1/users/me/capabilities`, () =>
    HttpResponse.json({
      user_id: MOCK_USER_ID,
      organization_id: MOCK_ORG_ID,
      capabilities: capsFor(MOCK_USER_ID),
    }),
  ),
  http.get(`${M}/v1/capabilities`, () => HttpResponse.json(CAPABILITY_DICT)),
  http.post(`${M}/v1/users/:userId/capabilities`, async ({ params, request }) => {
    const body = (await request.json()) as {
      organization_id: string;
      capabilities: string[];
    };
    const userId = params.userId as string;
    // Non-member rejection stand-in: an unknown target with no seeded row.
    if (!capStore[userId] && userId === "00000000-0000-0000-0000-000000000bad") {
      return HttpResponse.json(
        {
          detail:
            "El usuario destino no es miembro de esta organización (no tiene una asignación de rol activa).",
        },
        { status: 409 },
      );
    }
    const merged = Array.from(new Set([...(capStore[userId] ?? []), ...body.capabilities])).sort();
    capStore[userId] = merged;
    return HttpResponse.json({
      user_id: userId,
      organization_id: body.organization_id,
      capabilities: merged,
    });
  }),
  http.delete(`${M}/v1/users/:userId/capabilities`, async ({ params, request }) => {
    const body = (await request.json()) as {
      organization_id: string;
      capabilities: string[];
    };
    const userId = params.userId as string;
    const current = capStore[userId] ?? [];
    const remaining = current.filter((c) => !body.capabilities.includes(c));
    // Last-ACL-manager protection stand-in: block removing acl:manage from the
    // sole holder (the demo user).
    if (
      body.capabilities.includes("acl:manage") &&
      userId === MOCK_USER_ID &&
      !Object.entries(capStore).some(
        ([id, caps]) => id !== userId && caps.includes("acl:manage"),
      )
    ) {
      return HttpResponse.json(
        {
          detail: "No se puede quitar el último administrador de permisos de la organización.",
        },
        { status: 409 },
      );
    }
    capStore[userId] = remaining;
    return HttpResponse.json({
      user_id: userId,
      organization_id: body.organization_id,
      capabilities: remaining,
    });
  }),

  // ── UOM reference data (Oxbo-M) ──
  http.get(`${M}/v1/uom/profiles`, () => HttpResponse.json(PROFILE_LIST)),
  http.get(`${M}/v1/uom/profiles/:key/resolved`, ({ params }) => {
    const prof = RESOLVED_PROFILES[params.key as string];
    return prof ? HttpResponse.json(prof) : new HttpResponse(null, { status: 404 });
  }),
  // Org has no default profile set (mirrors the live 4xx) → Auto follows records.
  http.get(`${M}/v1/uom/org/effective`, () =>
    HttpResponse.json({ detail: "No default unit-system profile set" }, { status: 404 }),
  ),

  // ── Field-units registry (Oxbo-P) ──
  http.get(`${P}/v1/meta/field-units`, () => HttpResponse.json(FIELD_UNITS)),

  // ─────────────────────────── Oxbo-P ───────────────────────────
  http.get(`${P}/v1/wells/:uwi/events`, ({ params }) => {
    const uwi = decodeURIComponent(params.uwi as string);
    return HttpResponse.json(EVENTS_BY_WELL_UWI[uwi] ?? []);
  }),

  http.get(`${P}/v1/events/:eventId/reports`, ({ params }) => {
    const list = reportsByEvent[params.eventId as string] ?? [];
    // Newest report first, matching a typical server ordering.
    return HttpResponse.json([...list].sort((a, b) => b.report_no - a.report_no));
  }),

  http.get(`${P}/v1/reports/:id`, ({ params }) => {
    const r = reports[params.id as string];
    return r ? HttpResponse.json(r) : new HttpResponse(null, { status: 404 });
  }),

  http.post(`${P}/v1/reports`, async ({ request }) => {
    const body = (await request.json()) as DailyReportCreate;
    const id = `ddr-${body.event_id}-${body.report_no}`;
    const created: DailyReportOut = {
      event_id: body.event_id,
      well_uwi: body.well_uwi,
      report_no: body.report_no,
      date_report: body.date_report,
      comment_summary: body.comment_summary ?? null,
      comment_forecast_24: body.comment_forecast_24 ?? null,
      supervisor_1_entry_key: body.supervisor_1_entry_key ?? null,
      md_current: body.md_current ?? null,
      tvd_current: body.tvd_current ?? null,
      id,
      state: "open",
      is_last_of_event: true,
      // Server would compute these; mock returns plausible derived values.
      days_total: body.report_no,
      days_op: body.report_no,
      pct_complete: null,
      rop_avg_day: null,
      daily_cost: null,
    };
    reports[id] = created;
    (reportsByEvent[body.event_id] ??= []).push(created);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.patch(`${P}/v1/reports/:id`, async ({ params, request }) => {
    const id = params.id as string;
    const existing = reports[id];
    if (!existing) return new HttpResponse(null, { status: 404 });
    const patch = (await request.json()) as DailyReportUpdate;
    const updated: DailyReportOut = { ...existing, ...patch };
    reports[id] = updated;
    const list = reportsByEvent[existing.event_id];
    if (list) {
      const idx = list.findIndex((r) => r.id === id);
      if (idx >= 0) list[idx] = updated;
    }
    return HttpResponse.json(updated);
  }),

  http.post(`${P}/v1/reports/:id/close`, ({ params }) => {
    const id = params.id as string;
    const existing = reports[id];
    if (!existing) return new HttpResponse(null, { status: 404 });
    reports[id] = { ...existing, state: "closed" };
    const resp: ReportCloseResponse = { id, state: "closed", warnings: [], closed: true };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/cost-pivot`, ({ params, request }) => {
    const dim = new URL(request.url).searchParams.get("dimension") ?? "vendor";
    const set = COST_PIVOTS[params.eventId as string];
    const rows = set?.[dim] ?? [];
    const total = rows.reduce((s, r) => s + r.total, 0);
    const resp: CostPivotResponse = { dimension: dim, rows, total, currency: "USD" };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/plan-vs-actual`, ({ params }) => {
    const pv = PLAN_VS_ACTUAL[params.eventId as string];
    const by_phase = pv?.by_phase ?? [];
    const afe_total = by_phase.reduce((s, p) => s + p.afe, 0);
    const actual_total = by_phase.reduce((s, p) => s + p.actual, 0);
    const resp: PlanVsActualResponse = {
      afe_total,
      actual_total,
      variance: actual_total - afe_total,
      by_phase,
      currency: "USD",
    };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/npt-rollup`, ({ params }) => {
    const n = NPT_ROLLUPS[params.eventId as string];
    if (!n) {
      const empty: NptRollupResponse = {
        total_hours: 0,
        npt_hours: 0,
        npt_pct: null,
        by_class: [],
      };
      return HttpResponse.json(empty);
    }
    const resp: NptRollupResponse = {
      total_hours: n.total_hours,
      npt_hours: n.npt_hours,
      npt_pct: n.total_hours ? (n.npt_hours / n.total_hours) * 100 : null,
      by_class: n.by_class,
    };
    return HttpResponse.json(resp);
  }),

  // Cross-well feed (§3 / §10.0). Honours state / event_type / well_uwi /
  // from / to / mine filters and paginates by a numeric offset cursor.
  http.get(`${P}/v1/inbox`, ({ request }) => {
    const q = new URL(request.url).searchParams;
    const wellUwi = q.get("well_uwi") ?? "";
    const state = q.get("state") ?? "";
    const eventType = q.get("event_type") ?? "";
    const from = q.get("from") ?? "";
    const to = q.get("to") ?? "";
    const mine = q.get("mine") === "true";
    const limit = Number(q.get("limit") ?? "") || 25;
    const cursor = Number(q.get("cursor") ?? "") || 0;

    // Newest first — the default triage order.
    const filtered = INBOX.filter((i) => {
      if (wellUwi && i.well_uwi !== wellUwi) return false;
      if (state && i.state !== state) return false;
      if (eventType && i.event_type !== eventType) return false;
      if (from && i.date_report < from) return false;
      if (to && i.date_report > to) return false;
      // `mine` is a JWT-subject scope on the live API; the mock stands in with a
      // fixed demo supervisor so the toggle visibly narrows the feed.
      if (mine && i.supervisor_1_entry_key !== "sup.halvorsen") return false;
      return true;
    }).sort((a, b) => (a.date_report < b.date_report ? 1 : -1));

    const page = filtered.slice(cursor, cursor + limit);
    const nextOffset = cursor + limit;
    const resp: InboxResponse = {
      items: page,
      next_cursor: nextOffset < filtered.length ? String(nextOffset) : null,
    };
    return HttpResponse.json(resp);
  }),

  // Saved queries (§4) — stateful in-memory store so create/delete round-trip.
  http.get(`${P}/v1/saved-queries`, () => HttpResponse.json(savedQueries)),
  http.post(`${P}/v1/saved-queries`, async ({ request }) => {
    const body = (await request.json()) as SavedQueryCreate;
    if (body.visibility !== "private" && body.visibility !== "public") {
      return new HttpResponse(JSON.stringify({ detail: "visibility must be private|public" }), {
        status: 422,
      });
    }
    const created: SavedQueryOut = {
      id: `sq-${Date.now()}-${Math.floor(Math.random() * 1e4)}`,
      name: body.name,
      description: body.description ?? null,
      spec: body.spec ?? {},
      visibility: body.visibility,
      owner_user_id: MOCK_USER_ID,
    };
    savedQueries = [created, ...savedQueries];
    return HttpResponse.json(created, { status: 201 });
  }),
  http.delete(`${P}/v1/saved-queries/:id`, ({ params }) => {
    const id = params.id as string;
    const before = savedQueries.length;
    savedQueries = savedQueries.filter((q) => q.id !== id);
    if (savedQueries.length === before) {
      return new HttpResponse(JSON.stringify({ detail: "Saved query not found." }), { status: 404 });
    }
    return new HttpResponse(null, { status: 204 });
  }),

  // ─────────────── P3 domain reads (§5–§13) — MSW stand-ins ───────────────
  // Surveys (§5) — keyed by well UWI.
  http.get(`${P}/v1/wells/:uwi/surveys`, ({ params }) => {
    const uwi = decodeURIComponent(params.uwi as string);
    return HttpResponse.json(SURVEYS[uwi] ?? []);
  }),

  // Tubulars (§6) — casing/liner strings, running assemblies, and the pipe
  // tally, keyed by event. Casing/liner string types split from the running
  // assemblies; the live contract returns { assemblies, casing, pipe_tally }.
  http.get(`${P}/v1/events/:eventId/tubulars`, ({ params }) => {
    const eventId = params.eventId as string;
    const strings = TUBULAR_STRINGS[eventId] ?? [];
    const CASING_TYPES = new Set(["COND", "SURF", "INT", "PROD", "LINER"]);
    const casing = strings.filter((s) => CASING_TYPES.has(s.string_type_entry_key));
    const assemblies = strings.filter((s) => !CASING_TYPES.has(s.string_type_entry_key));
    const pipe_tally = strings.flatMap((s) => PIPE_TALLY[s.id] ?? []);
    return HttpResponse.json({ assemblies, casing, pipe_tally });
  }),

  // BHA (§7) — runs, each with its nested assembly + bit-ops grid, by event.
  http.get(`${P}/v1/events/:eventId/bha-runs`, ({ params }) => {
    const eventId = params.eventId as string;
    const runs = (BHA_RUNS[eventId] ?? []).map((r) => ({
      ...r,
      assembly: BHA_COMPONENTS[r.id] ?? [],
      bit_ops: BIT_OPS[r.id] ?? [],
    }));
    return HttpResponse.json({ runs });
  }),

  // Mud (§8) — a report's day (checks + products + transactions + volumes) +
  // an event's check trend.
  http.get(`${P}/v1/reports/:reportId/mud`, ({ params }) => {
    const reportId = params.reportId as string;
    const checks = Object.values(MUD_CHECKS)
      .flat()
      .filter((c) => c.report_id === reportId);
    const products = MUD_PRODUCTS[reportId] ?? [];
    const transactions = MUD_TRANSACTIONS[reportId] ?? [];
    const volumes = MUD_VOLUMES[reportId] ?? null;
    return HttpResponse.json({ checks, products, transactions, volumes });
  }),
  http.get(`${P}/v1/events/:eventId/mud-checks`, ({ params }) => {
    return HttpResponse.json(MUD_CHECKS[params.eventId as string] ?? []);
  }),

  // ── Time-log activities (§ time-entry) — read + CRUD, in-memory for the demo.
  http.get(`${P}/v1/reports/:reportId/activities`, ({ params }) => {
    return HttpResponse.json(ACTIVITIES[params.reportId as string] ?? []);
  }),
  http.post(`${P}/v1/reports/:reportId/activities`, async ({ params, request }) => {
    const reportId = params.reportId as string;
    const body = (await request.json()) as Record<string, unknown>;
    const stamp = `act-${Date.now()}`;
    const row = {
      id: stamp,
      entity_id: stamp,
      version: 1,
      status: "active",
      daily_report_id: reportId,
      event_id: (body.event_id as string) ?? "",
      well_uwi: null,
      wellbore_id: null,
      sequence_no: (ACTIVITIES[reportId]?.length ?? 0) + 1,
      time_class: "productive",
      time_from: null,
      time_to: null,
      ...body,
    };
    (ACTIVITIES[reportId] ??= []).push(row as (typeof ACTIVITIES)[string][number]);
    return HttpResponse.json(row, { status: 201 });
  }),
  // PATCH/DELETE key off the STABLE entity_id (Pattern-L), matching the real wire.
  http.patch(`${P}/v1/activities/:entityId`, async ({ params, request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    for (const list of Object.values(ACTIVITIES)) {
      const row = list.find((a) => a.entity_id === params.entityId);
      if (row) {
        Object.assign(row, body, { version: (row.version ?? 1) + 1 });
        return HttpResponse.json(row);
      }
    }
    return new HttpResponse(null, { status: 404 });
  }),
  http.delete(`${P}/v1/activities/:entityId`, ({ params }) => {
    for (const key of Object.keys(ACTIVITIES)) {
      const list = ACTIVITIES[key] ?? [];
      const row = list.find((a) => a.entity_id === params.entityId);
      if (row) {
        ACTIVITIES[key] = list.filter((a) => a.entity_id !== params.entityId);
        return HttpResponse.json({ ...row, status: "deleted" });
      }
    }
    return new HttpResponse(null, { status: 404 });
  }),

  // HSE (§9) — a report's daily HSE header + an event's LTI rollup + trail.
  // Safety is a daily header (LTI counters + day checks), not action rows.
  http.get(`${P}/v1/reports/:reportId/safety`, ({ params }) => {
    const day = SAFETY_DAYS[params.reportId as string];
    return day ? HttpResponse.json(day) : new HttpResponse(null, { status: 404 });
  }),
  http.get(`${P}/v1/events/:eventId/safety`, ({ params }) => {
    const roll = SAFETY_ROLLUPS[params.eventId as string];
    return roll ? HttpResponse.json(roll) : new HttpResponse(null, { status: 404 });
  }),

  // AFE lines (§11) — the line grid + accrual method, keyed by event.
  http.get(`${P}/v1/events/:eventId/afe-lines`, ({ params }) => {
    return HttpResponse.json(afeByEvent[params.eventId as string] ?? []);
  }),

  // Create a line. The server (mock) fills the money roll-ups + computes accrued
  // per §21f — a fresh plan line has committed/accrued/actual = 0 until ops run.
  http.post(`${P}/v1/events/:eventId/afe-lines`, async ({ params, request }) => {
    const eventId = params.eventId as string;
    const body = (await request.json()) as AfeLineCreate;
    const line: AfeLineOut = {
      id: `afe-${eventId}-${Date.now()}`,
      event_id: eventId,
      account_entry_key: body.account_entry_key,
      description: body.description ?? null,
      program_step: body.program_step ?? null,
      accrual_method: body.accrual_method,
      unit: body.unit ?? null,
      qty: body.qty ?? null,
      rate: body.rate ?? null,
      budget: body.budget,
      committed: 0,
      accrued: 0,
      actual: 0,
      status: "pending",
      currency: "USD",
      owner_party: body.owner_party ?? "operator",
      is_additional: body.is_additional ?? false,
    };
    line.accrued = computeAccrued(line);
    (afeByEvent[eventId] ??= []).push(line);
    return HttpResponse.json(line, { status: 201 });
  }),

  // Edit a line (headline case: re-point the accrual method). The server
  // recomputes accrued (§21f) from the new method and returns the updated line.
  http.patch(`${P}/v1/events/:eventId/afe-lines/:entityId`, async ({ params, request }) => {
    const eventId = params.eventId as string;
    const entityId = params.entityId as string;
    const list = afeByEvent[eventId];
    const idx = list?.findIndex((l) => l.id === entityId) ?? -1;
    if (!list || idx < 0) return new HttpResponse(null, { status: 404 });
    const patch = (await request.json()) as AfeLineUpdate;
    const updated: AfeLineOut = { ...list[idx]!, ...patch };
    updated.accrued = computeAccrued(updated);
    list[idx] = updated;
    return HttpResponse.json(updated);
  }),

  // Pad rollup (§12) — per-well time+cost + shared cost lines, keyed by site.
  // Fall back to the demo pad for any unseeded key so a repointed prod site_id
  // (e.g. SITE-MRPYQNS7) still renders in the mock/standalone build.
  http.get(`${P}/v1/pads/:siteId/rollup`, ({ params }) => {
    const roll = PAD_ROLLUPS[params.siteId as string] ?? PAD_ROLLUPS["site-nesson"];
    return roll ? HttpResponse.json(roll) : new HttpResponse(null, { status: 404 });
  }),

  // Pad shared-cost lines (§12) — the lines + server-computed allocation splits.
  http.get(`${P}/v1/pads/:siteId/shared-costs`, ({ params }) => {
    const site = params.siteId as string;
    const sc = PAD_SHARED_COSTS[site];
    return sc ? HttpResponse.json(sc) : HttpResponse.json({ site_id: site, lines: [] });
  }),
  http.post(`${P}/v1/pads/:siteId/shared-costs`, async ({ params, request }) => {
    const site = params.siteId as string;
    const body = (await request.json()) as PadSharedCostCreate;
    const sc = (PAD_SHARED_COSTS[site] ??= { site_id: site, currency: "USD", lines: [] });
    const line = {
      id: `sc-${Date.now()}`,
      cost_class_entry_key: body.cost_class_entry_key,
      description: body.description ?? null,
      vendor_entry_key: body.vendor_entry_key ?? null,
      amount: body.amount,
      allocation_method: body.allocation_method,
    };
    sc.lines = [...sc.lines, { ...line, split: computeMockSplit(site, line) }];
    return HttpResponse.json(sc, { status: 201 });
  }),
  http.patch(`${P}/v1/pads/:siteId/shared-costs/:lineId`, async ({ params, request }) => {
    const site = params.siteId as string;
    const body = (await request.json()) as PadSharedCostUpdate;
    const sc = PAD_SHARED_COSTS[site];
    if (!sc) return new HttpResponse(null, { status: 404 });
    sc.lines = sc.lines.map((l) => {
      if (l.id !== params.lineId) return l;
      const merged = { ...l, ...body };
      return { ...merged, split: computeMockSplit(site, merged) };
    });
    return HttpResponse.json(sc);
  }),

  // Timeline nodes (§16e) — the canonical flat node stream, keyed by event.
  http.get(`${P}/v1/events/:eventId/timeline-nodes`, ({ params }) => {
    return HttpResponse.json(TIMELINE_NODES[params.eventId as string] ?? []);
  }),

  // Pad Gantt (§12) — multi-track plan+actual for the pad's CPE + wells.
  http.get(`${P}/v1/pads/:siteId/timeline`, ({ params }) => padTimeline(params.siteId as string)),
  // Plan timeline (§13/§13a/§19) — multi-track plan+actual per hierarchy level.
  // Fleet = portfolio (CPE units × pads); pad = site_id; well = uwi; event =
  // event_id (section/ops). Empty tracks for an unknown scope, not a 404, so the
  // Planner renders the level chrome + an empty-state rather than an error.
  http.get(`${P}/v1/fleet/plan-timeline`, () => {
    return HttpResponse.json(
      PLAN_TIMELINES.fleet ?? { level: "fleet", scope: "fleet", tracks: [] },
    );
  }),
  http.get(`${P}/v1/pads/:siteId/plan-timeline`, ({ params }) => {
    const pt = PLAN_TIMELINES[params.siteId as string];
    return HttpResponse.json(pt ?? { level: "pad", scope: params.siteId, tracks: [] });
  }),
  http.get(`${P}/v1/wells/:uwi/plan-timeline`, ({ params }) => {
    const pt = PLAN_TIMELINES[params.uwi as string];
    return HttpResponse.json(pt ?? { level: "well", scope: params.uwi, tracks: [] });
  }),
  http.get(`${P}/v1/events/:eventId/plan-timeline`, ({ params }) => {
    const pt = PLAN_TIMELINES[params.eventId as string];
    return HttpResponse.json(pt ?? { level: "event", scope: params.eventId, tracks: [] });
  }),
];

function padTimeline(siteId: string) {
  const pt = PLAN_TIMELINES[siteId];
  return pt
    ? HttpResponse.json(pt)
    : HttpResponse.json({ level: "pad", scope: siteId, tracks: [] });
}
